import { CropDragHint } from './CropDragHint';
import { CropGuides } from './CropGuides';
import { placementFor, type CropPan, type PhotoSize } from './photo-crop';
import { useCropGestures } from './use-crop-gestures';
import { useFocusRequest } from './use-focus-request';

import '../venue-booking-content.css';

export interface PhotoCropBoxProps {
  url: string;
  size: PhotoSize;
  zoom: number;
  maxZoom: number;
  pan: CropPan;
  setZoom: (zoom: number) => void;
  setPan: (update: (pan: CropPan) => CropPan) => void;
  /** Ids of the text that explains the box, read out with it. */
  describedBy: string;
  focusRequested: boolean;
  onFocused: () => void;
}

/** The photo inside a fixed 16:9 box. Drag or use the arrow keys to move it; the box never changes shape. */
export function PhotoCropBox({
  url,
  size,
  zoom,
  maxZoom,
  pan,
  setZoom,
  setPan,
  describedBy,
  focusRequested,
  onFocused,
}: PhotoCropBoxProps) {
  const place = placementFor(size, zoom, pan);
  const { dragging, handlers } = useCropGestures({
    zoom,
    maxZoom,
    overhang: { x: (place.widthPct - 100) / 100, y: (place.heightPct - 100) / 100 },
    setZoom,
    setPan,
  });
  const box = useFocusRequest<HTMLDivElement>(focusRequested, onFocused);
  const movable = place.widthPct > 100.5 || place.heightPct > 100.5;

  return (
    <div
      {...handlers}
      ref={box}
      aria-describedby={describedBy}
      tabIndex={0}
      role="application"
      aria-label="Crop area. Drag or use the arrow keys to reposition the photo."
      className="vbc-crop-box"
      style={{
        position: 'relative',
        width: '100%',
        aspectRatio: '16 / 9',
        borderRadius: 10,
        overflow: 'hidden',
        background: '#1b2219',
        touchAction: 'none',
        userSelect: 'none',
        cursor: dragging ? 'grabbing' : 'grab',
        boxShadow: '0 0 0 2px var(--nb-primary)',
      }}
    >
      <img
        src={url}
        alt=""
        draggable={false}
        className="vbc-crop-image"
        style={{
          position: 'absolute',
          maxWidth: 'none',
          width: `${place.widthPct}%`,
          height: `${place.heightPct}%`,
          left: `${place.leftPct}%`,
          top: `${place.topPct}%`,
          transition: dragging ? 'none' : undefined,
        }}
      />
      <CropGuides />
      {!dragging && movable && <CropDragHint />}
    </div>
  );
}
